import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  PlayIcon,
  DocumentTextIcon
} from '@heroicons/react/24/solid';
import {
  ScissorsIcon,
  SparklesIcon,
  FaceSmileIcon,
  HeartIcon
} from '@heroicons/react/24/outline'; // Using these as placeholders for categories
import carouselHair from '../assets/carousel-hair.png';
import carouselBarber from '../assets/carousel-barber.png';
import carouselMassage from '../assets/carousel-massage.png';
import serviceManicure from '../assets/service-manicure.png';
import serviceMakeup from '../assets/service-makeup.png';
import serviceSkincare from '../assets/service-skincare.png';

const categories = [
  { name: 'Cabelereiro', icon: 'scissors' },
  { name: 'Barbeiros', icon: 'face' },
  { name: 'Manicure', icon: 'sparkles' },
  { name: 'Cuidados com a pele', icon: 'heart' },
  { name: 'Sobrancelhas e cílios', icon: 'eye' },
  { name: 'Dia de SPA', icon: 'star' },
  { name: 'Maquiagem', icon: 'brush' },
  { name: 'Saúde e bem-estar', icon: 'plus' },
  { name: 'Massoterapeuta', icon: 'hand' },
];

const recommendedSpots = [
  { id: 1, name: 'Studio Glamour', image: carouselHair, type: 'Cabelereiro', rating: 4.9, reviews: 120 },
  { id: 2, name: 'Barbearia Viking', image: carouselBarber, type: 'Barbearia', rating: 4.8, reviews: 85 },
  { id: 3, name: 'Zen Spa', image: carouselMassage, type: 'Massagem', rating: 5.0, reviews: 200 },
  { id: 4, name: 'Nails & Co.', image: serviceManicure, type: 'Manicure', rating: 4.7, reviews: 60 },
  { id: 5, name: 'MakeUp Art', image: serviceMakeup, type: 'Maquiagem', rating: 4.9, reviews: 45 },
  { id: 6, name: 'Pele de Seda', image: serviceSkincare, type: 'Estética', rating: 4.8, reviews: 90 },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [carouselHair, carouselBarber, carouselMassage];

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden">

      {/* Header Overlay */}
      <nav className="fixed top-0 w-full z-50 px-6 py-4 flex justify-between items-center text-white bg-slate-950/30 backdrop-blur-md border-b border-white/5 transition-all duration-300">
        <div className="flex items-center gap-2">
          <Link to="/" className="text-2xl font-black tracking-tight flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-emerald-500/30">
              H
            </div>
            <span>
              HoraMarcada<span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300 font-extrabold">Pro</span>
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-6 text-sm font-semibold">
          <Link to="/business" className="hidden border border-white/20 rounded-full px-5 py-2 hover:bg-white/10 hover:border-white/40 transition duration-300 sm:block shadow-sm">
            Listar sua empresa
          </Link>
          <Link to="/login" className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full transition duration-300 shadow-md shadow-emerald-500/20 hover:shadow-lg">
            Entrar / Criar Conta
          </Link>
        </div>
      </nav>

      {/* Hero Carousel */}
      <section className="relative h-[650px] bg-slate-950 overflow-hidden flex items-center justify-center">
        {/* Carousel Images */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-55' : 'opacity-0'}`}
          >
            <img src={slide} alt="Background" className="w-full h-full object-cover scale-105" />
          </div>
        ))}
        
        {/* Overlay Gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/20 to-slate-950"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"></div>

        {/* Hero Content */}
        <div className="relative z-10 text-center w-full max-w-4xl px-4 mt-20 animate-fade-in-up">
          <div className="bg-slate-900/30 backdrop-blur-xl border border-white/10 p-8 md:p-12 rounded-[2rem] shadow-2xl mb-8">
            <h1 className="text-4xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-emerald-200 mb-6 tracking-tight drop-shadow-lg">
              Seja ousado.
            </h1>
            <p className="text-slate-200/90 text-lg md:text-2xl mb-8 font-medium max-w-2xl mx-auto leading-relaxed">
              Descubra e reserve os melhores profissionais de beleza e bem-estar perto de você.
            </p>

            {/* Search Bar */}
            <div className="bg-white/95 rounded-full p-2 flex items-center shadow-2xl max-w-2xl mx-auto h-16 border border-white/20 transition-all duration-300 hover:shadow-emerald-500/10 focus-within:ring-2 focus-within:ring-emerald-500/30">
              <MagnifyingGlassIcon className="h-6 w-6 text-slate-400 ml-4 flex-shrink-0" />
              <input
                type="text"
                placeholder="Pesquise serviços, salões ou clínicas..."
                className="flex-1 px-4 text-slate-800 bg-transparent outline-none text-base md:text-lg h-full"
              />
              <div className="h-8 w-px bg-slate-200 mx-2 hidden sm:block"></div>
              <div className="hidden sm:flex items-center gap-2 px-4 text-slate-500 hover:text-slate-800 cursor-pointer transition">
                <MapPinIcon className="h-5 w-5 text-emerald-500" />
                <span className="font-semibold text-sm">Sua localização</span>
              </div>
              <button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full px-8 h-full font-bold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 shadow-md shadow-emerald-500/20 hover:shadow-lg transform active:scale-95 hidden sm:block">
                Buscar
              </button>
            </div>
          </div>
        </div>

        {/* Categories Bottom Bar */}
        <div className="absolute bottom-4 w-full z-20 py-4 overflow-x-auto no-scrollbar">
          <div className="max-w-7xl mx-auto px-6 flex justify-start md:justify-center gap-4 min-w-max">
            {categories.map((cat, idx) => (
              <Link 
                key={idx} 
                to="/explorar" 
                className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/10 hover:border-white/30 rounded-full transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-emerald-500/5 group text-white cursor-pointer backdrop-blur-md"
              >
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full group-hover:scale-125 transition-transform"></span>
                <span className="text-xs font-bold uppercase tracking-wider">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section - Video & PDF */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Subtle Background Art */}
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="relative z-10 animate-fade-in-up">
              <div className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 font-bold text-xs uppercase tracking-wider mb-6">
                Como Funciona
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight tracking-tight">
                Simples, rápido e <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500 relative">
                  totalmente intuitivo
                  <svg className="absolute w-full h-3 -bottom-2 left-0 text-emerald-200/60 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                  </svg>
                </span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed font-medium">
                Descubra como o HoraMarcada Pro revoluciona a gestão do seu negócio ou a forma como você agenda serviços. Assista ao vídeo para ver a plataforma em ação.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="flex items-center justify-center gap-3 px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 transform active:scale-95">
                  <PlayIcon className="h-5 w-5 text-emerald-400" />
                  <span>Ver Vídeo Demo</span>
                </button>
                <a href="#" className="flex items-center justify-center gap-3 px-8 py-4 bg-slate-50 text-slate-800 border border-slate-200 rounded-full font-bold hover:border-slate-300 hover:bg-slate-100 transition duration-300">
                  <DocumentTextIcon className="h-5 w-5 text-slate-500" />
                  <span>Baixar Apresentação (PDF)</span>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 pt-8 border-t border-slate-100 flex items-center gap-12">
                <div>
                  <p className="text-3xl font-black text-slate-900">5k+</p>
                  <p className="text-sm font-bold text-slate-500">Profissionais ativos</p>
                </div>
                <div className="w-px h-10 bg-slate-200"></div>
                <div>
                  <p className="text-3xl font-black text-slate-900">100k+</p>
                  <p className="text-sm font-bold text-slate-500">Agendamentos mensais</p>
                </div>
              </div>
            </div>

            {/* Right Content - Video Placeholder */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white dark:border-slate-800 bg-slate-900 aspect-video group cursor-pointer">
                {/* Decorative Background for Video */}
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?q=80&w=2574&auto=format&fit=crop')] bg-cover bg-center opacity-60 group-hover:opacity-45 transition duration-500 transform group-hover:scale-105"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent"></div>

                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center group-hover:scale-110 transition duration-300 border border-white/40 shadow-2xl">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <PlayIcon className="h-8 w-8 text-emerald-600 ml-1" />
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <div className="absolute bottom-0 left-0 w-full p-6 text-white z-10">
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-1">Demonstração</p>
                  <h3 className="text-xl font-bold">Tour Completo pela Plataforma</h3>
                  <p className="text-xs text-white/70 mt-1">Duração: 2:30 min</p>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-2xl border border-slate-50 hidden md:block animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <SparklesIcon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase">Resultado Real</p>
                    <p className="text-sm font-black text-slate-800">+30% Faturamento</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended Section (Real Images) */}
      <section className="py-24 bg-slate-50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in-up">
            <div className="inline-block px-4 py-1 rounded-full bg-slate-200/60 text-slate-700 font-bold text-xs uppercase tracking-wider mb-3">
              Descubra
            </div>
            <h2 className="text-4xl font-black text-slate-900 mb-3 tracking-tight">Recomendados para você</h2>
            <p className="text-slate-500 font-medium text-lg">Estabelecimentos parceiros avaliados com excelência máxima no ranking.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-16">
            {recommendedSpots.map((spot) => (
              <div key={spot.id} className="bg-white rounded-3xl shadow-md shadow-slate-100 hover:shadow-2xl hover:shadow-emerald-500/5 border border-slate-100 overflow-hidden group cursor-pointer transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between">
                <div className="relative h-60 overflow-hidden">
                  <img src={spot.image} alt={spot.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-black flex items-center gap-1 text-slate-900 shadow-md">
                    <span className="text-yellow-500 text-sm">★</span> {spot.rating} <span className="text-slate-400 font-bold">({spot.reviews})</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-1.5">{spot.type}</p>
                  <h3 className="text-xl font-black text-slate-900 mb-4 group-hover:text-emerald-600 transition-colors">{spot.name}</h3>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-sm text-slate-500 font-semibold group-hover:text-slate-800 transition">
                    <span>Ver horários</span>
                    <span className="text-lg transform group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-6 text-center">
            <Link to="/explorar" className="inline-flex items-center gap-2 text-emerald-600 font-bold hover:text-emerald-700 transition duration-300">
              Ver todos os estabelecimentos <span className="text-xl font-bold leading-none">→</span>
            </Link>

            <div className="w-full h-px bg-slate-200 max-w-xs mx-auto"></div>

            <Link to="/business" className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-bold text-base hover:shadow-xl transform hover:-translate-y-0.5 transition duration-300 active:scale-95">
              Listar sua empresa grátis
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Minimal */}
      <footer className="bg-white py-12 border-t border-slate-100 text-center">
        <p className="text-slate-400 text-sm font-semibold">© 2026 HoraMarcada Pro. Todos os direitos reservados.</p>
        <div className="flex justify-center gap-8 mt-4 text-sm font-bold text-slate-500">
          <Link to="/business" className="hover:text-emerald-600 transition">Para Empresas</Link>
          <Link to="/terms" className="hover:text-emerald-600 transition">Termos</Link>
          <Link to="/privacy" className="hover:text-emerald-600 transition">Privacidade</Link>
        </div>
      </footer>

    </div>
  );
}